import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { trackPageview, attachScrollDepth, resetScrollSeen } from "./lib/tracker";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import ResultsPage from "./pages/ResultsPage";
import ForAttorneysPage from "./pages/ForAttorneysPage";
import AttorneyAuthPage from "./pages/AttorneyAuthPage";
import AttorneyDashboardPage from "./pages/AttorneyDashboardPage";
import AdminAuthPage from "./pages/AdminAuthPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AuthCallback from "./pages/AuthCallback";
import PlanComparisonPage from "./pages/PlanComparisonPage";
import ForAffiliatesPage from "./pages/ForAffiliatesPage";
import AffiliateAuthPage from "./pages/AffiliateAuthPage";
import AffiliateDashboardPage from "./pages/AffiliateDashboardPage";
import JoinRedirectPage from "./pages/JoinRedirectPage";
import AttorneyPublicProfilePage from "./pages/AttorneyPublicProfilePage";
import FindAttorneyPage from "./pages/FindAttorneyPage";
import LocationDirectoryPage from "./pages/LocationDirectoryPage";
import PublicTrafficReportPage from "./pages/PublicTrafficReportPage";

// Mounted inside BrowserRouter so useLocation works. Fires a pageview on
// every route change and re-arms scroll-depth tracking per page.
function RouteTracker() {
    const location = useLocation();
    useEffect(() => {
        resetScrollSeen();
        trackPageview(location.pathname + location.search);
        attachScrollDepth();
    }, [location.pathname, location.search]);
    return null;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <RouteTracker />
                <Layout>
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/results" element={<ResultsPage />} />
                        <Route path="/for-attorneys" element={<ForAttorneysPage />} />
                        <Route path="/for-attorneys/pricing" element={<PlanComparisonPage />} />
                        <Route path="/plans" element={<Navigate to="/for-attorneys/pricing" replace />} />
                        {/* Backwards-compatible aliases */}
                        <Route path="/pricing" element={<Navigate to="/for-attorneys/pricing" replace />} />
                        <Route path="/categories" element={<Navigate to="/#categories" replace />} />
                        <Route path="/attorney/auth" element={<AttorneyAuthPage />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/attorney/dashboard" element={<AttorneyDashboardPage />} />
                        <Route path="/admin/auth" element={<AdminAuthPage />} />
                        <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                        {/* Affiliate / referral partner side */}
                        <Route path="/for-affiliates" element={<ForAffiliatesPage />} />
                        <Route path="/affiliate/auth" element={<AffiliateAuthPage />} />
                        <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
                        <Route path="/join" element={<JoinRedirectPage />} />
                        {/* Public attorney profile (singular). */}
                        <Route path="/attorney/:attorneyId" element={<AttorneyPublicProfilePage />} />
                        {/* Legacy plural URL — keep working so any older links/emails redirect cleanly. */}
                        <Route path="/attorneys/profile/:attorneyId" element={<AttorneyPublicProfilePage />} />
                        {/* Directory + SEO location pages. */}
                        <Route path="/find-an-attorney" element={<FindAttorneyPage />} />
                        <Route path="/attorneys" element={<FindAttorneyPage />} />
                        <Route path="/attorneys/:stateSlug" element={<LocationDirectoryPage />} />
                        <Route path="/attorneys/:stateSlug/:countySlug" element={<LocationDirectoryPage />} />
                        <Route path="/attorneys/:stateSlug/:countySlug/:categorySlug" element={<LocationDirectoryPage />} />
                        {/* Public shareable traffic-report link */}
                        <Route path="/r/:token" element={<PublicTrafficReportPage />} />
                    </Routes>
                </Layout>
                <Toaster position="top-right" />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
